// src/admin/pages/AdminCustomers.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { 
    FaSearch, 
    FaUserSlash, 
    FaUserShield, 
    FaEye 
} from "react-icons/fa";
import {
  getAdminCustomers,
  toggleAdminCustomerStatus,
} from "../../services/adminCustomers.api";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getCommunityEmails } from "../../services/adminCommunity.api";

function AdminCustomers() {
    const [isOpen, setIsOpen] = useState(false);

    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const navigate = useNavigate();
    const { lang } = useContext(AdminLanguageContext);

    const [whatsappOpen, setWhatsappOpen] = useState(false);
    const [whatsappMessage, setWhatsappMessage] = useState("");

    const [emailOpen, setEmailOpen] = useState(false);
    const [emailMessage, setEmailMessage] = useState("");
    const [emailSubject, setEmailSubject] = useState(""); 

    const [listOpen, setListOpen] = useState(false);
    const [listType, setListType] = useState(null); 

    const [communityOpen, setCommunityOpen] = useState(false);
const [communityEmails, setCommunityEmails] = useState([]);
const [communityPage, setCommunityPage] = useState(1);

const PER_PAGE = 10;

const [communityEmailOpen, setCommunityEmailOpen] = useState(false);
const [communityEmailSubject, setCommunityEmailSubject] = useState("");
const [communityEmailMessage, setCommunityEmailMessage] = useState("");

    
    const printList = () => {
  window.print();
};


    const TEXT = {
        EN: {
            title: "Customers",
            search: "Search customers...",
            name: "Name",
            email: "Email",
            phone: "Phone",
            orders: "Orders",
            totalSpent: "Total Spent",
            lastOrder: "Last Order",
            status: "Status",
            actions: "Actions",
            loading: "Loading customers...",
            empty: "No customers found.",
            view: "View",
            toggle: "Toggle",
            totalCustomers: "Total customers",
            sendWhatsapp: "Send WhatsApp Message",
            noPhones: "No customers with phone numbers",
            reset: "Reset",
            today: "Today",
            thisWeek: "This Week",
            thisMonth: "This Month",
            reset: "Reset",
        },
        AR: {
            title: "العملاء",
            search: "ابحث عن العملاء...",
            name: "الاسم",
            email: "البريد الإلكتروني",
            phone: "الهاتف",
            orders: "الطلبات",
            totalSpent: "إجمالي الإنفاق",
            lastOrder: "آخر طلب",
            status: "الحالة",
            actions: "الإجراءات",
            loading: "جاري تحميل العملاء...",
            empty: "لا يوجد عملاء.",
            view: "عرض",
            toggle: "تغيير الحالة",
            totalCustomers: "إجمالي العملاء",
            sendWhatsapp: "إرسال رسالة واتساب",
            noPhones: "لا يوجد عملاء لديهم أرقام هاتف",
            reset: "إعادة تعيين",
            today: "اليوم",
            thisWeek: "هذا الأسبوع",
            thisMonth: "هذا الشهر",
            reset: "إعادة تعيين",
        },
    }[lang];

    const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
const perms = JSON.parse(localStorage.getItem("staffPermissions") || "{}");
const isAdmin = adminUser.role === "admin";

const canAccess = isAdmin || perms.can_manage_customers;


    useEffect(() => {
  setLoading(true);

  getAdminCustomers()
    .then(setCustomers)
    .catch(console.error)
    .finally(() => setLoading(false));
}, []);


    const q = search.toLowerCase();

const filtered = customers.filter((c) => {
  // 🔍 text search
  const matchesSearch =
    (c.name || "").toLowerCase().includes(q) ||
    (c.email || "").toLowerCase().includes(q) ||
    (c.phone || "").toLowerCase().includes(q);

  if (!matchesSearch) return false;

  // 📅 created_at filter (TIMEZONE-SAFE)
  if (dateFrom || dateTo) {
    const raw = c.created_at ?? c.createdAt;
    if (!raw) return false;

    const d = raw instanceof Date ? raw : new Date(raw);
    if (isNaN(d.getTime())) return false;

    // ✅ LOCAL YYYY-MM-DD (NO UTC SHIFT)
    const createdYMD =
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0");

    if (dateFrom && createdYMD < dateFrom) return false;
    if (dateTo && createdYMD > dateTo) return false;
  }

  return true;
});




    const toggleStatus = async (id) => {
  await toggleAdminCustomerStatus(id);

  setCustomers((prev) =>
    prev.map((c) =>
      c.id === id
        ? { ...c, status: c.status === "active" ? "blocked" : "active" }
        : c
    )
  );
};

const applyQuickDateFilter = (type) => {
  const today = new Date();

  const toYMD = (d) =>
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0");

  if (type === "today") {
    const ymd = toYMD(today);
    setDateFrom(ymd);
    setDateTo(ymd);
  }

  if (type === "week") {
    const start = new Date(today);
    start.setDate(today.getDate() - 6); // last 7 days
    setDateFrom(toYMD(start));
    setDateTo(toYMD(today));
  }

  if (type === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(toYMD(start));
    setDateTo(toYMD(today));
  }
};

const resetFilters = () => {
  setSearch("");
  setDateFrom("");
  setDateTo("");
};

const sendWhatsappMessage = () => {
  const phones = filtered
    .map(c => c.phone)
    .filter(Boolean)
    .map(p => p.replace(/[^\d]/g, ""));

  if (phones.length === 0) {
    alert(TEXT.noPhones);
    return;
  }

  // ✅ COPY ALL NUMBERS (comma-separated) for WhatsApp Broadcast
  navigator.clipboard.writeText(phones.join(","));

  // ✅ Use admin-written message from modal
  const message = encodeURIComponent(
    whatsappMessage || "Hello 👋"
  );

  // ✅ Open WhatsApp Business
  window.location.href = `whatsapp://send?text=${message}`;
};

const sendEmailMessage = () => {
  const emails = filtered
    .map(c => c.email)
    .filter(Boolean);

  if (emails.length === 0) {
    alert(lang === "AR" ? "لا يوجد بريد إلكتروني" : "No customer emails found");
    return;
  }

  // ✅ Copy emails for backup / reuse
  navigator.clipboard.writeText(emails.join(","));

  const subject = encodeURIComponent(emailSubject || "Our Latest Updates");
  const body = encodeURIComponent(emailMessage || "Hello 👋");

  // ✅ BCC = all customers
  const mailto = `mailto:?bcc=${emails.join(",")}&subject=${subject}&body=${body}`;

  window.location.href = mailto;
};

const openCommunityEmails = async () => {
  try {
    const data = await getCommunityEmails();
    setCommunityEmails(data);
    setCommunityPage(1);
    setCommunityOpen(true);
  } catch (e) {
    alert(lang === "AR" ? "فشل تحميل الإيميلات" : "Failed to load emails");
  }
};


const sendCommunityEmails = () => {
  if (communityEmails.length === 0) {
    alert(lang === "AR" ? "لا يوجد إيميلات" : "No community emails");
    return;
  }

  const emails = communityEmails.map(e => e.email);

  const subject = encodeURIComponent(
    communityEmailSubject || "Community Updates"
  );

  const body = encodeURIComponent(
    communityEmailMessage || "Hello 👋"
  );

  // optional copy for backup
  navigator.clipboard.writeText(emails.join(","));

  const mailto = `mailto:?bcc=${emails.join(",")}&subject=${subject}&body=${body}`;
  window.location.href = mailto;

  // cleanup
  setCommunityEmailOpen(false);
  setCommunityEmailSubject("");
  setCommunityEmailMessage("");
};



    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 w-full transition-all duration-300">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">
  {!canAccess && (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
      <p className="text-red-700 font-semibold">
        {lang === "AR"
          ? "ليس لديك صلاحية للوصول إلى هذه الصفحة"
          : "You do not have permission to access this page."}
      </p>
    </div>
  )}

  {canAccess && (
    <>

                <div className="print:hidden">
                    {/* ===== TITLE ROW ===== */}
<div className="mb-4">
  <h1 className="text-2xl font-bold text-cyan-700">
    {TEXT.title}
  </h1>
</div>

{/* ===== SEARCH + FILTERS ROW ===== */}
<div className="mb-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">

  {/* SEARCH */}
  <div className="relative lg:col-span-4">
    <FaSearch className="absolute left-3 top-3 text-gray-400" />
    <input
      type="text"
      placeholder={TEXT.search}
      className="w-full pl-10 p-3 border rounded-xl shadow-sm"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />
  </div>

  {/* DATE FROM */}
  <div className="lg:col-span-2">
    <input
      type="date"
      value={dateFrom}
      onChange={(e) => setDateFrom(e.target.value)}
      className="w-full p-3 border rounded-xl shadow-sm text-sm"
    />
  </div>

  {/* DATE TO */}
  <div className="lg:col-span-2">
    <input
      type="date"
      value={dateTo}
      onChange={(e) => setDateTo(e.target.value)}
      className="w-full p-3 border rounded-xl shadow-sm text-sm"
    />
  </div>

  {/* QUICK FILTERS */}
  <div className="lg:col-span-4 flex flex-wrap gap-2">
    <button
  onClick={() => applyQuickDateFilter("today")}
  className="px-3 py-2 rounded-lg border bg-white hover:bg-cyan-50 text-sm"
>
  {TEXT.today}
</button>

    <button
  onClick={() => applyQuickDateFilter("week")}
  className="px-3 py-2 rounded-lg border bg-white hover:bg-cyan-50 text-sm"
>
  {TEXT.thisWeek}
</button>

    <button
  onClick={() => applyQuickDateFilter("month")}
  className="px-3 py-2 rounded-lg border bg-white hover:bg-cyan-50 text-sm"
>
  {TEXT.thisMonth}
</button>

    <button
  onClick={resetFilters}
  className="px-3 py-2 rounded-lg border bg-red-50 text-red-600 hover:bg-red-100 text-sm"
>
  {TEXT.reset}
</button>
  </div>
</div>

{/* ===== TOTAL + ACTION BUTTONS ROW ===== */}
<div className="mb-6 flex flex-wrap items-center justify-between gap-3">

  {/* TOTAL COUNT */}
  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 border text-sm text-gray-700">
    <span className="font-semibold text-gray-800">
      {TEXT.totalCustomers}:
    </span>
    <span className="font-bold text-cyan-700">
      {loading ? "..." : filtered.length}
    </span>
  </div>

  {/* ACTION BUTTONS */}
  <div className="flex flex-wrap gap-2">
  <button
  onClick={openCommunityEmails}
  className="px-4 py-2 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800"
>
  📬 {lang === "AR" ? "إيميلات المجتمع" : "Community Emails"}
</button>


    <button
      onClick={() => {
        setListType("phone");
        setListOpen(true);
      }}
      className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700"
    >
      📞 {lang === "AR" ? "أرقام الهاتف" : "Phone Numbers"}
    </button>

    <button
      onClick={() => {
        setListType("email");
        setListOpen(true);
      }}
      className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
    >
      📧 {lang === "AR" ? "البريد الإلكتروني" : "Emails"}
    </button>
  </div>
</div>



                    {/* DESKTOP TABLE */}
                    <div className="hidden md:block bg-white shadow rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-[900px] w-full">
                                <thead className="bg-gray-200 text-gray-700">
                                    <tr>
                                        <th className="p-3 text-center">{TEXT.name}</th>
                                        <th className="p-3 text-center">{TEXT.email}</th>
                                        <th className="p-3 text-center">{TEXT.phone}</th>
                                        <th className="p-3 text-center">{TEXT.orders}</th>
                                        <th className="p-3 text-center">{TEXT.totalSpent}</th>
                                        <th className="p-3 text-center">{TEXT.lastOrder}</th>
                                        <th className="p-3 text-center">{TEXT.status}</th>
                                        <th className="p-3 text-center">{TEXT.actions}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td className="p-4 text-center" colSpan="8">
                                                {TEXT.loading}
                                            </td>
                                        </tr>
                                    ) : filtered.length === 0 ? (
                                        <tr>
                                            <td className="p-4 text-center" colSpan="8">
                                                {TEXT.empty}
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((c) => (
                                            <tr key={c.id} className="border-b hover:bg-gray-50">
                                                <td className="p-3 font-semibold text-center">{c.name}</td>
                                                <td className="p-3 text-center">{c.email}</td>
                                                <td className="p-3 text-center">{c.phone}</td>
                                                <td className="p-3 text-center">{c.orders}</td>

                                                <td className="p-3 text-center font-bold text-green-700">
                                                    ${c.totalSpent}
                                                </td>

                                                <td className="p-3 text-center">{c.lastOrder}</td>

                                                <td className="p-3 text-center">
                                                    <span
                                                        className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                                            c.status === "active"
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-red-100 text-red-700"
                                                        }`}
                                                    >
                                                        {c.status}
                                                    </span>
                                                </td>

                                                <td className="p-3 text-center flex items-center justify-center gap-3">
                                                    <button
                                                        className="text-cyan-600 hover:text-cyan-800"
                                                        onClick={() => navigate(`/admin/customers/${c.id}`)}
                                                    >
                                                        <FaEye />
                                                    </button>

                                                    <button
                                                        className={`${
                                                            c.status === "active"
                                                                ? "text-yellow-600 hover:text-yellow-800"
                                                                : "text-green-600 hover:text-green-800"
                                                        }`}
                                                        onClick={() => toggleStatus(c.id)}
                                                    >
                                                        {c.status === "active" ? (
                                                            <FaUserSlash />
                                                        ) : (
                                                            <FaUserShield />
                                                        )}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* MOBILE VIEW */}
                    <div className="md:hidden space-y-3">
                        {loading ? (
                            <div className="bg-white p-4 rounded-xl shadow text-center">
                                {TEXT.loading}
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="bg-white p-4 rounded-xl shadow text-center">
                                {TEXT.empty}
                            </div>
                        ) : (
                            filtered.map((c) => (
                                <div
                                    key={c.id}
                                    className="bg-white p-4 rounded-xl shadow border"
                                >
                                    <h2 className="font-bold text-lg text-gray-800">
                                        {c.name}
                                    </h2>

                                    <p className="text-sm text-gray-600">
                                        <span className="font-semibold">{TEXT.email}:</span> {c.email}
                                    </p>

                                    <p className="text-sm text-gray-600">
                                        <span className="font-semibold">{TEXT.phone}:</span> {c.phone}
                                    </p>

                                    <p className="text-sm">
                                        <span className="font-semibold">{TEXT.orders}:</span> {c.orders}
                                    </p>

                                    <p className="text-sm">
                                        <span className="font-semibold">{TEXT.totalSpent}:</span> ${c.totalSpent}
                                    </p>

                                    <p className="text-sm">
                                        <span className="font-semibold">{TEXT.lastOrder}:</span> {c.lastOrder}
                                    </p>

                                    <p className="mt-2">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                c.status === "active"
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-red-100 text-red-700"
                                            }`}
                                        >
                                            {c.status}
                                        </span>
                                    </p>

                                    <div className="flex gap-6 mt-4">
                                        <button
                                            className="flex items-center gap-1 text-cyan-600 hover:text-cyan-800 text-sm"
                                            onClick={() => navigate(`/admin/customers/${c.id}`)}
                                        >
                                            <FaEye /> {TEXT.view}
                                        </button>

                                        <button
                                            className={`flex items-center gap-1 text-sm ${
                                                c.status === "active"
                                                    ? "text-yellow-600 hover:text-yellow-800"
                                                    : "text-green-600 hover:text-green-800"
                                            }`}
                                            onClick={() => toggleStatus(c.id)}
                                        >
                                            {c.status === "active" ? <FaUserSlash /> : <FaUserShield />} {TEXT.toggle}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>


                    {emailOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-3">
        {lang === "AR" ? "إرسال بريد إلكتروني" : "Send Email"}
      </h3>

      <input
        value={emailSubject}
        onChange={(e) => setEmailSubject(e.target.value)}
        placeholder={lang === "AR" ? "عنوان الرسالة" : "Email subject"}
        className="w-full p-3 border rounded-xl mb-3 text-sm"
      />

      <textarea
        rows="6"
        value={emailMessage}
        onChange={(e) => setEmailMessage(e.target.value)}
        placeholder={lang === "AR" ? "اكتب الرسالة هنا..." : "Write your email here..."}
        className="w-full p-3 border rounded-xl text-sm"
      />

      <div className="flex justify-end gap-3 mt-5">
        <button
          onClick={() => setEmailOpen(false)}
          className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-sm"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={sendEmailMessage}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold"
        >
          📧 {lang === "AR" ? "إرسال" : "Send"}
        </button>
      </div>
    </div>
  </div>
)}

{communityOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          {lang === "AR" ? "إيميلات المجتمع" : "Community Emails"}
        </h3>

        <button
          onClick={() => setCommunityOpen(false)}
          className="px-3 py-1 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm"
        >
          {lang === "AR" ? "إغلاق" : "Close"}
        </button>
      </div>

      {/* TABLE */}
      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">#</th>
              <th className="p-3 text-left">
                {lang === "AR" ? "البريد الإلكتروني" : "Email"}
              </th>
              <th className="p-3 text-left">
                {lang === "AR" ? "تاريخ الاشتراك" : "Joined At"}
              </th>
            </tr>
          </thead>

          <tbody>
            {communityEmails
              .slice(
                (communityPage - 1) * PER_PAGE,
                communityPage * PER_PAGE
              )
              .map((e, i) => (
                <tr key={e.id} className="border-t">
                  <td className="p-3">
                    {(communityPage - 1) * PER_PAGE + i + 1}
                  </td>
                  <td className="p-3 font-medium">{e.email}</td>
                  <td className="p-3 text-gray-500">
                    {new Date(e.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {communityEmails.length > PER_PAGE && (
        <div className="flex justify-between items-center mt-4">
          <button
            disabled={communityPage === 1}
            onClick={() => setCommunityPage(p => p - 1)}
            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-50"
          >
            ◀
          </button>

          <span className="text-sm font-semibold">
            {communityPage} / {Math.ceil(communityEmails.length / PER_PAGE)}
          </span>

          <button
            disabled={communityPage * PER_PAGE >= communityEmails.length}
            onClick={() => setCommunityPage(p => p + 1)}
            className="px-4 py-2 rounded-lg bg-gray-200 disabled:opacity-50"
          >
            ▶
          </button>
        </div>
      )}

    </div>
  </div>
)}


                    {communityEmailOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">

      <h3 className="text-lg font-bold text-gray-800 mb-3">
        {lang === "AR"
          ? "إرسال بريد إلى مجتمع المشتركين"
          : "Send Email to Community"}
      </h3>

      <input
        value={communityEmailSubject}
        onChange={(e) => setCommunityEmailSubject(e.target.value)}
        placeholder={lang === "AR" ? "عنوان الرسالة" : "Email subject"}
        className="w-full p-3 border rounded-xl mb-3 text-sm"
      />

      <textarea
        rows="6"
        value={communityEmailMessage}
        onChange={(e) => setCommunityEmailMessage(e.target.value)}
        placeholder={
          lang === "AR"
            ? "اكتب محتوى الرسالة هنا..."
            : "Write your email content here..."
        }
        className="w-full p-3 border rounded-xl text-sm"
      />

      <div className="flex justify-end gap-3 mt-5">
        <button
          onClick={() => setCommunityEmailOpen(false)}
          className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-sm"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={sendCommunityEmails}
          className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold"
        >
          📧 {lang === "AR" ? "إرسال" : "Send"}
        </button>
      </div>
    </div>
  </div>
)}


                    {whatsappOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-3">
        {TEXT.sendWhatsapp}
      </h3>

      <textarea
        rows="5"
        value={whatsappMessage}
        onChange={(e) => setWhatsappMessage(e.target.value)}
        placeholder={
          lang === "AR"
            ? "اكتب رسالة واتساب هنا..."
            : "Write your WhatsApp message here..."
        }
        className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-green-500 text-sm"
      />

      <div className="flex justify-end gap-3 mt-5">
        <button
          onClick={() => setWhatsappOpen(false)}
          className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-sm"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={sendWhatsappMessage}
          className="px-4 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-semibold"
        >
          🟢 {TEXT.sendWhatsapp}
        </button>
      </div>
    </div>
  </div>
)}

</div>

{listOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 print:bg-white print:flex print:items-start">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 print:shadow-none print:rounded-none">
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <h3 className="text-lg font-bold text-gray-800">
          {listType === "phone"
            ? lang === "AR" ? "قائمة أرقام الهاتف" : "Phone Numbers List"
            : lang === "AR" ? "قائمة البريد الإلكتروني" : "Emails List"}
        </h3>

        <div className="flex gap-2">
          <button
            onClick={printList}
            className="px-4 py-2 rounded-xl bg-gray-800 text-white text-sm hover:bg-black"
          >
            🖨 Print
          </button>

          <button
            onClick={() => setListOpen(false)}
            className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-sm"
          >
            {lang === "AR" ? "إغلاق" : "Close"}
          </button>
        </div>
      </div>

      {/* TABLE SCROLL CONTAINER */}
<div className="max-h-[65vh] overflow-y-auto border border-gray-300 print:max-h-full print:overflow-visible">
  <table className="w-full text-sm">
        <thead className="bg-gray-200">
          <tr>
            <th className="border p-2 text-left">
              {lang === "AR" ? "الاسم الكامل" : "Full Name"}
            </th>
            <th className="border p-2 text-left">
              {listType === "phone"
                ? lang === "AR" ? "رقم الهاتف" : "Phone Number"
                : lang === "AR" ? "البريد الإلكتروني" : "Email"}
            </th>
          </tr>
        </thead>

        <tbody>
          {filtered
            .filter(c => listType === "phone" ? c.phone : c.email)
            .map((c, i) => (
              <tr key={i}>
                <td className="border p-2">{c.name}</td>
                <td className="border p-2">
                  {listType === "phone" ? c.phone : c.email}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
     </div>
    </div>
  </div>
)}
            </>
  )}


                </main>
        
            </div>
        </div>
    );
}

export default AdminCustomers;
